import { useMemo } from 'react';
import { actionFigures, actionPoint, focusIds } from '../../engine/display/focus';

/** The display's condition strip: four chips, then "+n" (the Focus rulings); the classic map keeps its own. */
const DISPLAY_CHIPS = 4;

/** Off the display: nothing is dimmed, faded or x-rayed, and no option applies. */
const NO_ACTION = Object.freeze({
  focus: null,
  focusPoint: null,
  xrayFigures: null,
  chevronTarget: null,
  pills: false,
  numbersOff: false,
  maxChips: undefined,
  ambient: null,
  heat: false,
  actorId: null,
});

/**
 * useDisplayAction - what the display shows of the action, worked out once
 * (plan map-display, C): who it is about (`pair`, the pair the camera
 * framed), the quiet map's focus, the point the scene fades from, the figures
 * the x-ray keeps seen, the chevron's target, the ambient marks (T4), and the
 * options that gate each.
 * Off the display, NO_ACTION.
 *
 * @param {object} a
 * @param {boolean} a.shown - the display is the renderer
 * @param {object} a.prefs - the viewer's display options (useDisplayPrefs)
 * @param {{actorId: string|null, targetId: string|null}} a.pair
 * @param {Record<string, object>} a.tokens - the markers by id
 * @param {object[]} a.tokenList - the markers on the map
 * @param {string|null} a.hovered - the marker under the pointer, which counts as in focus
 * @param {Record<string, object>} [a.drawnTokens] - the markers where they are drawn this frame (a walker mid-route)
 */
export default function useDisplayAction({ shown, prefs, pair, tokens, tokenList, hovered, drawnTokens = tokens }) {
  const { actorId, targetId } = pair;
  const quiet = shown && prefs.quiet;
  const focus = useMemo(
    () => (quiet ? focusIds(tokenList, actorId, targetId, hovered) : null),
    [quiet, tokenList, actorId, targetId, hovered],
  );
  const scene = useMemo(
    () => ({
      focusPoint: shown && prefs.depth ? actionPoint(tokens, actorId, targetId) : null,
      xrayFigures: shown && prefs.xray ? actionFigures(tokens, [actorId, targetId]) : null,
    }),
    [shown, prefs.depth, prefs.xray, tokens, actorId, targetId],
  );
  const ambient = useAmbient(prefs, drawnTokens, actorId, targetId);
  if (!shown) return NO_ACTION;
  return {
    focus,
    ...scene,
    chevronTarget: quiet ? (tokens[targetId] ?? null) : null,
    pills: prefs.pills,
    numbersOff: prefs.numbersOff,
    maxChips: DISPLAY_CHIPS,
    ambient,
    heat: !!prefs.heat,
    // Whom the ambient marks are about (the pair's actor): the heat marks the other side from them too.
    actorId,
  };
}

/**
 * The ambient marks on the figures as drawn (a walker's wedge goes with it), hung on the three switches and the two
 * figures alone: anybody else walking leaves them as they were.
 */
function useAmbient(prefs, drawnTokens, actorId, targetId) {
  const { sightWedge, scanlines, vignette } = prefs;
  const actor = (actorId && drawnTokens[actorId]) || null;
  const target = (targetId && drawnTokens[targetId]) || null;
  return useMemo(
    () => ambientOf({ sightWedge, scanlines, vignette }, actor, target),
    [sightWedge, scanlines, vignette, actor, target],
  );
}

/**
 * The ambient marks the scene draws (plan design-gaps, T4), each by its own switch: the sight wedge from whoever acts
 * toward the one they target, the scanlines, the frame's vignette.
 */
function ambientOf(prefs, actor, target) {
  return {
    wedge: prefs.sightWedge && actor && target && actor !== target ? { from: actor, toward: target } : null,
    scanlines: !!prefs.scanlines,
    vignette: !!prefs.vignette,
  };
}
